import { Injectable, signal, computed } from '@angular/core';
import { environment } from '../../../environments/environment';
import { COMMON_CONSTANTS } from '../constants/common.constants';
import {
  defaultEmailIntegrationFallback,
  normalizeEmailIntegrationData,
  type EmailIntegrationFromOption,
} from '../models/email-integration-data';

/**
 * ATS `/users/logout` from localStorage `atsThDomain` only.
 * Strips trailing `/signin` when present, otherwise appends `/users/logout`.
 */
export function resolveAtsUsersLogoutUrl(atsHost: string): string {
  const trimmed = atsHost.trim();
  if (!trimmed) {
    return '';
  }
  const base = /^https?:\/\//i.test(trimmed)
    ? trimmed.replace(/\/$/, '')
    : `https://${trimmed.replace(/^\/+/, '').replace(/\/$/, '')}`;
  if (/\/signin$/i.test(base)) {
    return base.replace(/\/signin$/i, '/users/logout');
  }
  return `${base}/users/logout`;
}

function pickString(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!obj) return '';
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return '?';
}

/** Auth + profile from localStorage (token + JSON user payload). */
@Injectable({ providedIn: 'root' })
export class UserService {
  readonly ATS_TOKEN_STORAGE_KEY = COMMON_CONSTANTS.JWT_TOKEN_KEY;
  readonly USER_INFO_STORAGE_KEY = COMMON_CONSTANTS.USER_INFO_KEY;
  readonly TH_DOMAIN_STORAGE_KEY = COMMON_CONSTANTS.TH_DOMAIN_KEY;
  readonly EMAIL_INTEGRATION_DATA_KEY = COMMON_CONSTANTS.EMAIL_INTEGRATION_DATA_KEY;
  readonly DATE_FORMAT_STORAGE_KEY = COMMON_CONSTANTS.DATE_FORMAT_KEY;
  private _tokenSignal = signal<string | null>(null);
  private _userInfo = signal<Record<string, unknown> | null>(null);
  private _emailIntegrationData = signal<EmailIntegrationFromOption[]>([]);

  /** Sidebar / header: full name from API user payload */
  readonly displayName = computed(() => {
    const u = this._userInfo();
    return (
      pickString(u, ['name', 'userName', 'fullName', 'user_name', 'displayName', 'userFullName']) ||
      'Guest'
    );
  });

  /** Given name when API sends it (e.g. localhost); otherwise falls back to {@link displayName}. */
  readonly firstName = computed(() => {
    const u = this._userInfo();
    const fn = pickString(u, ['firstName', 'first_name', 'givenName', 'fname']);
    if (fn) return fn;
    return this.displayName();
  });

  /** e.g. role or plan line under the name */
  readonly accountSubtitle = computed(() => {
    const u = this._userInfo();
    return (
      pickString(u, ['roleName', 'role', 'accountType', 'plan', 'subscription', 'role_name']) || 'N/A'
    );
  });

  readonly userEmail = computed(() => {
    const u = this._userInfo();
    return pickString(u, ['email', 'userEmail', 'mail', 'workEmail']);
  });

  readonly userInitials = computed(() => {
    const name = this.displayName();
    if (name && name !== 'Guest') return initialsFromName(name);
    const email = this.userEmail();
    if (email) return email.slice(0, 2).toUpperCase();
    return 'G';
  });

  /** Cake `privileges` or `users.is_privilege` from session validate — token allocation admin gate. */
  readonly isAdministrator = computed(() => UserService.readAdministratorFlag(this._userInfo()));

  /** Compose From options from session validate; hydrated from localStorage on load. */
  readonly emailIntegrationData = computed(() => this._emailIntegrationData());

  readonly isAuthenticated = computed(() => {
    let token = this._tokenSignal();
    if (token == null && typeof window !== 'undefined') {
      try {
        token = localStorage.getItem(this.ATS_TOKEN_STORAGE_KEY);
        if (token) this._tokenSignal.set(token);
      } catch {
        this._tokenSignal.set(null);
      }
    }
    return !!token;
  });

  constructor() {
    this.initializeTokenFromStorage();
    this.refreshUserInfoSignal();
    this.hydrateEmailIntegrationDataFromStorage();
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key === this.ATS_TOKEN_STORAGE_KEY) this._tokenSignal.set(e.newValue);
        if (e.key === this.USER_INFO_STORAGE_KEY) this.refreshUserInfoSignal();
        if (e.key === this.EMAIL_INTEGRATION_DATA_KEY) this.hydrateEmailIntegrationDataFromStorage();
      });
    }
  }

  /** Company `date_format` from session validate (`config_company_settings`). */
  getCompanyDateFormat(): string {
    if (typeof window === 'undefined') return '';
    try {
      return localStorage.getItem(this.DATE_FORMAT_STORAGE_KEY)?.trim() ?? '';
    } catch {
      return '';
    }
  }

  setCompanyDateFormat(format: string): void {
    if (typeof window === 'undefined') return;
    const trimmed = format.trim();
    try {
      if (trimmed) {
        localStorage.setItem(this.DATE_FORMAT_STORAGE_KEY, trimmed);
      } else {
        localStorage.removeItem(this.DATE_FORMAT_STORAGE_KEY);
      }
    } catch (e) {
      console.error('[UserService] Failed to save company date format', e);
    }
  }

  clearCompanyDateFormat(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(this.DATE_FORMAT_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  /** Sync signal from localStorage (call after external updates) */
  refreshUserInfoSignal(): void {
    this._userInfo.set(this.getUserInfo());
  }

  isAuthenticatedMethod(): boolean {
    return this.isAuthenticated();
  }

  updateTokenSignal(): void {
    if (typeof window !== 'undefined') {
      try {
        const token = localStorage.getItem(this.ATS_TOKEN_STORAGE_KEY);
        this._tokenSignal.set(token);
      } catch {
        this._tokenSignal.set(null);
      }
    }
  }

  /** Persist user/profile object from authorization (or any) API */
  setUserInfo(info: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(this.USER_INFO_STORAGE_KEY, JSON.stringify(info));
      this._userInfo.set(info);
    } catch (e) {
      console.error('[UserService] Failed to save user info', e);
    }
  }

  /**
   * Persist `data.email_integration_data` from session validate for compose From dropdown.
   */
  setEmailIntegrationData(options: EmailIntegrationFromOption[]): void {
    const normalized = normalizeEmailIntegrationData(options);
    const toStore =
      normalized.length > 0 ? normalized : defaultEmailIntegrationFallback(this.displayName());
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(this.EMAIL_INTEGRATION_DATA_KEY, JSON.stringify(toStore));
      this._emailIntegrationData.set(toStore);
    } catch (e) {
      console.error('[UserService] Failed to save email integration data', e);
      this._emailIntegrationData.set(toStore);
    }
  }

  /**
   * From options for compose; falls back to default notifications@ + display name when storage is empty.
   */
  getEmailIntegrationData(): EmailIntegrationFromOption[] {
    const current = this._emailIntegrationData();
    if (current.length > 0) {
      return current;
    }
    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(this.EMAIL_INTEGRATION_DATA_KEY);
        if (raw) {
          const parsed = normalizeEmailIntegrationData(JSON.parse(raw) as unknown);
          if (parsed.length > 0) {
            this._emailIntegrationData.set(parsed);
            return parsed;
          }
        }
      } catch {
        /* ignore */
      }
    }
    return defaultEmailIntegrationFallback(this.displayName());
  }

  clearEmailIntegrationData(): void {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(this.EMAIL_INTEGRATION_DATA_KEY);
      } catch {
        /* ignore */
      }
    }
    this._emailIntegrationData.set([]);
  }

  private hydrateEmailIntegrationDataFromStorage(): void {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(this.EMAIL_INTEGRATION_DATA_KEY);
      if (!raw) {
        this._emailIntegrationData.set([]);
        return;
      }
      this._emailIntegrationData.set(normalizeEmailIntegrationData(JSON.parse(raw) as unknown));
    } catch {
      this._emailIntegrationData.set([]);
    }
  }

  /** `th_domain` from session validate (tenant ATS host); empty if never set or cleared. */
  getStoredThDomain(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      const v = localStorage.getItem(this.TH_DOMAIN_STORAGE_KEY);
      const t = v?.trim();
      if (t) {
        return t;
      }
      const u = this.getUserInfo();
      const fromUser = pickString(u, ['th_domain', 'thDomain', 'atsThDomain', 'tenantDomain', 'portalHost']);
      return fromUser ? fromUser : null;
    } catch {
      return null;
    }
  }

  /** `atsThDomain` in localStorage only — used for ATS `/users/logout` (no user-info or env fallbacks). */
  private getAtsThDomainFromLocalStorage(): string | null {
    if (typeof window === 'undefined') return null;
    try {
      const v = localStorage.getItem(this.TH_DOMAIN_STORAGE_KEY)?.trim();
      return v || null;
    } catch {
      return null;
    }
  }

  /** Read parsed user info from localStorage */
  getUserInfo(): Record<string, unknown> | null {
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(this.USER_INFO_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }

  clearUserInfo(): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(this.USER_INFO_STORAGE_KEY);
      this._userInfo.set(null);
    } catch {
      /* ignore */
    }
  }

  /**
   * Full sign-out: best-effort **`POST {BFFOrchApiUrl}/agent/logout`** (clears BFF Firestore ATS slice)
   * and **`POST {domainOrchApiUrl}/auth/logout`** (Bearer — deletes `ats_user_session_*`, matching
   * `ats_company_session_*`, and `buzz_auth_token_*` in Redis),
   * then clears `localStorage` / signals, then **`window.location`** to **`{atsThDomain}/users/logout`** when
   * localStorage `atsThDomain` is set (no env or user-info fallback).
   * The `redirectUrl` argument is kept for callers but the final navigation is always the resolved ATS logout URL.
   */
  logoutRedirect(redirectUrl: string): void {
    void this.runLogoutThenRedirect(redirectUrl);
  }

  /**
   * Expired or invalid token (401): clear client storage and go to ATS — does **not** call
   * `/users/logout` (session is already unusable).
   */
  redirectToAtsAfterInvalidSession(redirectUrl: string): void {
    this.clearClientAuthAndNavigate(redirectUrl);
  }

  private clearClientAuthAndNavigate(redirectUrl: string): void {
    if (typeof window === 'undefined') return;
    try {
      localStorage.clear();
    } catch {
      try {
        localStorage.removeItem(this.ATS_TOKEN_STORAGE_KEY);
        localStorage.removeItem(this.USER_INFO_STORAGE_KEY);
        localStorage.removeItem(this.TH_DOMAIN_STORAGE_KEY);
        localStorage.removeItem(this.EMAIL_INTEGRATION_DATA_KEY);
      } catch {
        /* ignore */
      }
    }
    this._tokenSignal.set(null);
    this._userInfo.set(null);
    this._emailIntegrationData.set([]);
    const url = redirectUrl?.trim();
    if (url) {
      window.location.assign(url);
    }
  }

  private async runLogoutThenRedirect(_redirectUrl: string): Promise<void> {
    if (typeof window === 'undefined') return;
    const tenantHost = this.getAtsThDomainFromLocalStorage();
    const logoutUrl = tenantHost ? resolveAtsUsersLogoutUrl(tenantHost) : '';
    const token = localStorage.getItem(this.ATS_TOKEN_STORAGE_KEY)?.trim();
    if (token) {
      const authHeaders = {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      };
      const bffBase = environment.BFFOrchApiUrl.replace(/\/$/, '');
      const domainBase = environment.domainOrchApiUrl.replace(/\/$/, '');
      await Promise.allSettled([
        fetch(`${bffBase}/agent/logout`, {
          method: 'POST',
          headers: authHeaders,
          credentials: 'omit',
          mode: 'cors',
          cache: 'no-store',
        }),
        fetch(`${domainBase}/auth/logout`, {
          method: 'POST',
          headers: authHeaders,
          credentials: 'omit',
          mode: 'cors',
          cache: 'no-store',
        }),
      ]);
    }
    this.clearClientAuthAndNavigate(logoutUrl);
  }

  private initializeTokenFromStorage(): void {
    if (typeof window !== 'undefined') {
      try {
        this._tokenSignal.set(localStorage.getItem(this.ATS_TOKEN_STORAGE_KEY));
      } catch {
        this._tokenSignal.set(null);
      }
    } else {
      this._tokenSignal.set(null);
    }
  }

  /** True when session user has administrator privileges (`privileges` or `is_privilege`). */
  static readAdministratorFlag(user: Record<string, unknown> | null | undefined): boolean {
    if (!user) {
      return false;
    }
    for (const key of ['privileges', 'isPrivilege', 'is_privilege']) {
      const v = user[key];
      if (v === 1 || v === '1' || v === true) {
        return true;
      }
    }
    return false;
  }
}
